// /reservations — Admin Reservations (PAGES.md §3.28)
//
// Cross-location reservation management with admin reporting KPIs.
// Same as POS §2.19 + LocationSwitcher + StatCards (total, no-show rate, peak hour).
// RESTAURANT vertical only — MANAGER+ (level 30).
// Traceability: FR-ORD-044, US-ORD-007

import { useState, useCallback, useEffect } from 'react';
import {
  PageContainer,
  Button,
  Select,
  Input,
  TextArea,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  Modal,
  ConfirmDialog,
  DatePicker,
  TimePicker,
  ReservationCard,
  Tooltip,
  useToast,
} from '@bbpos/ui';
import type { ReservationStatus } from '@bbpos/ui/src/components/RestaurantComponents/ReservationCard/types';
import { reservationService } from '@/services/reservation.service';
import type { Reservation, ReservationApiStatus, ReservationStats } from '@/services/reservation.service';
import { tableService } from '@/services/table.service';
import type { DiningTable } from '@/services/table.service';
import { buildTableListParams, resolveReservationLocationId } from './reservation-tables';
import { buildTabDateFilters, formatPeakHour } from './reservation-list';
import { toLocalIsoDate, todayISO } from './date-utils';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { GLASS, cn, toneClasses } from '@/constants/design-tokens';
import { ApiError } from '@/lib/api-client';
import { useTenantDial } from '@/hooks/use-tenant-dial';

// ─── Icons ──────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function CalendarEmptyIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

// ─── Constants ──────────────────────────────────────────────────────────────

const TAB_OPTIONS = [
  { key: 'today', label: 'Today' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
] as const;

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'SEATED', label: 'Seated' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'NO_SHOW', label: 'No Show' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const PARTY_SIZE_OPTIONS = Array.from({ length: 20 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1} guest${i > 0 ? 's' : ''}`,
}));

const DURATION_OPTIONS = [
  { value: '60', label: '60 min' },
  { value: '90', label: '90 min' },
  { value: '120', label: '120 min' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

function mapApiStatusToUI(status: ReservationApiStatus): ReservationStatus {
  const map: Record<ReservationApiStatus, ReservationStatus> = {
    CONFIRMED: 'confirmed',
    SEATED: 'arrived',
    COMPLETED: 'arrived',
    NO_SHOW: 'no_show',
    CANCELLED: 'cancelled',
  };
  return map[status] ?? 'pending';
}

function buildDateFromISO(date: string, time: string): string {
  // Tolerate either a clean date ("2026-05-18") or an ISO timestamp, and a
  // time of "HH:MM" or "HH:MM:SS" — so a stray shape never yields "Invalid Date".
  const datePart = (date ?? '').slice(0, 10);
  const timePart = (time ?? '').slice(0, 5) || '00:00';
  return `${datePart}T${timePart}:00`;
}

// ─── KPI Card ──────────────────────────────────────────────────────────────

function KpiMini({ label, value, tone }: { label: string; value: string | number; tone: 'brand' | 'success' | 'danger' | 'violet' | 'teal' }) {
  const tc = toneClasses(tone);
  return (
    <div className={cn(GLASS, 'relative overflow-hidden p-4')}>
      <div className={`absolute inset-0 bg-gradient-to-br ${tc.accent} pointer-events-none`} />
      <p className="relative text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="relative text-xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums font-mono mt-1">{value}</p>
    </div>
  );
}

// ─── Form ───────────────────────────────────────────────────────────────────

interface ReservationForm {
  customer_name: string;
  customer_phone: string;
  party_size: string;
  reservation_date: string;
  reservation_time: string;
  table_id: string;
  duration_minutes: string;
  notes: string;
}

const EMPTY_FORM: ReservationForm = {
  customer_name: '',
  customer_phone: '',
  party_size: '2',
  reservation_date: todayISO(),
  reservation_time: '19:00',
  table_id: '',
  duration_minutes: '90',
  notes: '',
};

// ─── Component ──────────────────────────────────────────────────────────────

function ReservationsContent() {
  const dial = useTenantDial();
  const { toast } = useToast();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  // ── State ───────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<string>('today');
  const [statusFilter, setStatusFilter] = useState('');
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<ReservationStats | null>(null);
  const PAGE_LIMIT = 50;

  // ── Modal state ─────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<ReservationForm>({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [tables, setTables] = useState<DiningTable[]>([]);

  // ── Confirm dialog ──────────────────────────────────────────────────────
  const [confirmAction, setConfirmAction] = useState<{
    id: string;
    action: 'SEATED' | 'COMPLETED' | 'NO_SHOW' | 'CANCELLED';
    label: string;
  } | null>(null);
  const [isActioning, setIsActioning] = useState(false);

  // ── Fetch ───────────────────────────────────────────────────────────────
  // Tabs are resolved server-side (date/date_after/date_before) so the list
  // paginates and the KPIs aggregate over the WHOLE set — not a capped,
  // client-filtered 200-row slice (GitHub issue #276).
  const fetchReservations = useCallback(async () => {
    setIsLoading(true);
    try {
      const scope = {
        location_id: selectedLocationId ?? undefined,
        status: statusFilter || undefined,
        ...buildTabDateFilters(activeTab, todayISO()),
      };
      const [result, statsResult] = await Promise.all([
        reservationService.listReservations({ ...scope, page, limit: PAGE_LIMIT }),
        reservationService.getStats(scope),
      ]);
      setReservations(result.data);
      setTotalPages(result.meta.totalPages);
      setTotalCount(statsResult.total);
      setStats(statsResult);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load reservations.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationId, activeTab, statusFilter, page, toast]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  // Any scope change (tab, status, location) resets to the first page so the
  // user never lands on an out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [activeTab, statusFilter, selectedLocationId]);

  // ── Fetch tables ────────────────────────────────────────────────────────
  const fetchTables = useCallback(async () => {
    try {
      // The table picker lists every active table in the tenant — it must NOT
      // be scoped to the global header location filter, or the dropdown goes
      // empty whenever a specific branch is selected (GitHub issue #73).
      const result = await tableService.list(buildTableListParams());
      setTables(result.data.filter((t: DiningTable) => t.is_active));
    } catch {
      // Silently fail
    }
  }, []);

  // ── Keyboard shortcut: n → New reservation ─────────────────────────────
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      )
        return;
      if (e.key === 'n' && !modalOpen) {
        e.preventDefault();
        openCreateModal();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen]);

  // ── KPI ─────────────────────────────────────────────────────────────────
  // Sourced from the server aggregate (whole set), never the current page.
  const confirmedCount = stats?.confirmed ?? 0;
  const noShowRate = stats?.no_show_rate ?? 0;
  const peakHour = formatPeakHour(stats?.peak_hour ?? null);

  // ── Modal ───────────────────────────────────────────────────────────────
  const openCreateModal = useCallback(() => {
    setForm({ ...EMPTY_FORM, reservation_date: todayISO() });
    setFormErrors({});
    setModalOpen(true);
    fetchTables();
  }, [fetchTables]);

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (!form.customer_name.trim()) errors.customer_name = 'Name is required';
    if (!form.customer_phone.trim()) errors.customer_phone = 'Phone is required';
    else if (!/^\+[1-9]\d{6,14}$/.test(form.customer_phone)) errors.customer_phone = 'Must be E.164 format';
    if (!form.table_id) errors.table_id = 'Select a table';
    if (!form.reservation_date) errors.reservation_date = 'Date is required';
    if (!form.reservation_time) errors.reservation_time = 'Time is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form]);

  const handleCreate = useCallback(async () => {
    if (!validateForm()) return;
    const selectedTable = tables.find((t) => t.id === form.table_id);
    const locationId = resolveReservationLocationId(selectedLocationId, selectedTable);
    if (!locationId) return;
    setIsSaving(true);
    try {
      await reservationService.createReservation({
        location_id: locationId,
        table_id: form.table_id,
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        party_size: Number(form.party_size),
        reservation_date: form.reservation_date,
        reservation_time: form.reservation_time,
        duration_minutes: Number(form.duration_minutes),
        notes: form.notes.trim() || undefined,
      });
      setModalOpen(false);
      toast({ title: 'Reservation created', variant: 'success' });
      fetchReservations();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.message.includes('conflict') || err.message.includes('TABLE_NOT_AVAILABLE')) {
          setFormErrors({ table_id: 'Table not available at that time' });
        } else {
          toast({ title: err.message, variant: 'error' });
          setModalOpen(false);
        }
      } else {
        toast({ title: 'Failed to create reservation', variant: 'error' });
        setModalOpen(false);
      }
    } finally {
      setIsSaving(false);
    }
  }, [validateForm, selectedLocationId, tables, form, fetchReservations, toast]);

  // ── Status update ───────────────────────────────────────────────────────
  const handleStatusUpdate = useCallback(async () => {
    if (!confirmAction) return;
    setIsActioning(true);
    try {
      await reservationService.updateStatus(confirmAction.id, { status: confirmAction.action });
      setConfirmAction(null);
      toast({ title: `Reservation marked as ${confirmAction.label}`, variant: 'success' });
      fetchReservations();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to update reservation.', variant: 'error' });
    } finally {
      setIsActioning(false);
    }
  }, [confirmAction, fetchReservations, toast]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleFormChange = useCallback((field: keyof ReservationForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleSeat = useCallback((id: string) => {
    setConfirmAction({ id, action: 'SEATED', label: 'Seated' });
  }, []);

  const handleCancel = useCallback((id: string) => {
    setConfirmAction({ id, action: 'CANCELLED', label: 'Cancelled' });
  }, []);

  const tableOptions = tables.map((t) => ({
    value: t.id,
    label: `${t.table_number}${t.seating_capacity ? ` (${t.seating_capacity} seats)` : ''}`,
  }));

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <PageContainer
      title="Reservations"
      subtitle={`${totalCount} reservation${totalCount !== 1 ? 's' : ''}`}
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Reservations' },
      ]}
      actions={
        <Tooltip content="Press N to add (keyboard shortcut)">
          <Button variant="primary" size="sm" onClick={openCreateModal}>
            <PlusIcon />
            <span className="ml-1.5">New Reservation</span>
          </Button>
        </Tooltip>
      }
    >
      <div className="animate-fade-in">
        {/* ═══ KPI Row ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiMini label="Total Reservations" value={totalCount} tone="violet" />
          <KpiMini label="Confirmed" value={confirmedCount} tone="brand" />
          <KpiMini label="No-Show Rate" value={`${noShowRate}%`} tone="danger" />
          <KpiMini label="Peak Hour" value={peakHour} tone="teal" />
        </div>

        {/* ═══ Filter Row ═══ */}
        <div className={cn(GLASS, 'p-4 mb-6')}>
          <div className="flex flex-wrap items-center gap-3">
            {/* Tabs */}
            <div className="flex items-center gap-1">
              {TAB_OPTIONS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={[
                    'shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all active:scale-95',
                    activeTab === tab.key
                      ? 'bg-violet-600 text-white shadow-md'
                      : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700',
                  ].join(' ')}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Status filter */}
            <div className="w-40 ml-auto">
              <Select
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(val) => setStatusFilter(typeof val === 'string' ? val : val[0] ?? '')}
                placeholder="All Statuses"
                clearable
              />
            </div>
          </div>
        </div>

        {/* ═══ Reservations Grid ═══ */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={cn(GLASS, 'p-5')}>
                <Skeleton variant="text" width="60%" height={20} className="mb-3" />
                <Skeleton variant="text" width="40%" height={16} className="mb-4" />
                <Skeleton variant="rect" width="100%" height={40} className="mb-3" />
                <div className="flex gap-2">
                  <Skeleton variant="rect" width={80} height={24} />
                  <Skeleton variant="rect" width={60} height={24} />
                </div>
              </div>
            ))}
          </div>
        ) : reservations.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<CalendarEmptyIcon />}
            title="No reservations"
            description="Reservations will appear here once created"
            action={{
              label: 'Add Reservation',
              onClick: openCreateModal,
            }}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reservations.map((r) => (
              <ReservationCard
                key={r.id}
                id={r.id}
                guestName={r.customer_name}
                partySize={r.party_size}
                phone={r.customer_phone}
                reservedAt={buildDateFromISO(r.reservation_date, r.reservation_time)}
                tableName={r.table_number ? `Table ${r.table_number}` : undefined}
                status={mapApiStatusToUI(r.status)}
                specialRequests={r.notes}
                onSeat={r.status === 'CONFIRMED' ? handleSeat : undefined}
                onCancel={
                  r.status !== 'CANCELLED' && r.status !== 'COMPLETED' && r.status !== 'NO_SHOW'
                    ? handleCancel
                    : undefined
                }
              />
            ))}
          </div>
        )}

        {/* ═══ Pagination ═══ */}
        {!isLoading && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
              Page {page} of {totalPages} · {totalCount.toLocaleString('en-IN')} reservation{totalCount !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Prev
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ═══ New Reservation Modal ═══ */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="New Reservation"
        variant="wide"
      >
        <div className="space-y-4 p-1">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Customer Name *</label>
            <Input
              value={form.customer_name}
              onChange={(val) => handleFormChange('customer_name', val)}
              placeholder="Rajesh Kumar"
              aria-label="Customer name"
            />
            {formErrors.customer_name && <p className="text-xs text-danger-600 mt-1">{formErrors.customer_name}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Phone *</label>
            <Input
              variant="phone"
              defaultDial={dial}
              value={form.customer_phone}
              onChange={(val) => handleFormChange('customer_phone', val)}
              placeholder="98765 43210"
              aria-label="Customer phone"
            />
            {formErrors.customer_phone && <p className="text-xs text-danger-600 mt-1">{formErrors.customer_phone}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Party Size *</label>
              <Select
                options={PARTY_SIZE_OPTIONS}
                value={form.party_size}
                onChange={(val) => handleFormChange('party_size', typeof val === 'string' ? val : val[0] ?? '2')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Duration</label>
              <Select
                options={DURATION_OPTIONS}
                value={form.duration_minutes}
                onChange={(val) => handleFormChange('duration_minutes', typeof val === 'string' ? val : val[0] ?? '90')}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Date *</label>
              <DatePicker
                value={form.reservation_date ? new Date(form.reservation_date + 'T00:00:00') : undefined}
                onChange={(val: Date) => handleFormChange('reservation_date', toLocalIsoDate(val))}
              />
              {formErrors.reservation_date && <p className="text-xs text-danger-600 mt-1">{formErrors.reservation_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Time *</label>
              <TimePicker
                value={form.reservation_time}
                onChange={(val) => handleFormChange('reservation_time', val)}
              />
              {formErrors.reservation_time && <p className="text-xs text-danger-600 mt-1">{formErrors.reservation_time}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Table *</label>
            <Select
              options={tableOptions}
              value={form.table_id}
              onChange={(val) => handleFormChange('table_id', typeof val === 'string' ? val : val[0] ?? '')}
              placeholder="Select a table"
            />
            {formErrors.table_id && <p className="text-xs text-danger-600 mt-1">{formErrors.table_id}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Special Requests</label>
            <TextArea
              value={form.notes}
              onChange={(val) => handleFormChange('notes', val)}
              placeholder="Window seat preferred, birthday celebration..."
              rows={3}
              aria-label="Special requests"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setModalOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreate} disabled={isSaving}>
              {isSaving ? 'Creating...' : 'Create Reservation'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ═══ Confirm Dialog ═══ */}
      <ConfirmDialog
        open={confirmAction !== null}
        title={`Mark as ${confirmAction?.label ?? ''}?`}
        description={`Are you sure you want to mark this reservation as ${confirmAction?.label?.toLowerCase() ?? ''}?`}
        confirmLabel={confirmAction?.label ?? 'Confirm'}
        variant={confirmAction?.action === 'CANCELLED' || confirmAction?.action === 'NO_SHOW' ? 'danger' : 'warning'}
        loading={isActioning}
        onConfirm={handleStatusUpdate}
        onCancel={() => setConfirmAction(null)}
      />
    </PageContainer>
  );
}

export function ReservationsPage() {
  return (
    <ErrorBoundary>
      <ReservationsContent />
    </ErrorBoundary>
  );
}
